import { useState } from 'react';

import { useQueries, useQueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';

import useRemoteFileUploadMutation from 'components/hooks/useRemoteFileUploadMutation';

export default function useAttachmentsUploader({
  onUploaderMutationError = async () => {},
  onUploaderMutationSuccess = async () => {},
  type,
}) {
  const queryClient = useQueryClient();
  const createRemoteFileUploadMutation = useRemoteFileUploadMutation();

  const [unprocessedFiles, setUnprocessedFiles] = useState([]);

  const uploaderQueries = useQueries({
    queries: unprocessedFiles.map((unprocessedFile) => ({
      queryKey: ['uploader', unprocessedFile.uuid],
      queryFn: async () => {
        const fileType =
          type ||
          (unprocessedFile.file.type.includes('image') ? 'image' : 'pdf');

        const response = await axios.get('/uploaders/new', {
          params: { type: fileType },
        });

        return response.data;
      },
      enabled: unprocessedFiles.length > 0,
    })),
  });

  const uploadAttachment = async (currentFile) => {
    const { file, metadata, uuid } = currentFile;
    const uploadResponse = queryClient.getQueryData(['uploader', uuid]);

    try {
      const uploadKey = await createRemoteFileUploadMutation.mutateAsync({
        file,
        uploadResponse,
      });
      await onUploaderMutationSuccess(uploadKey, file, metadata);
    } catch (error) {
      await onUploaderMutationError(file, error);
    }
  };

  const handleFilesSelect = (selectedFiles, metadata = {}) => {
    setUnprocessedFiles((prev) => [
      ...prev,
      ...selectedFiles.map((file) => ({
        file,
        metadata,
        uuid: uuidv4(),
      })),
    ]);
  };

  const queriesData = queryClient.getQueriesData(['uploader']);
  const queryUUIDsSet = new Set(queriesData.map((query) => query[0][1]));
  const allFilesHaveQueries =
    unprocessedFiles.length > 0 &&
    unprocessedFiles.every((file) => queryUUIDsSet.has(file.uuid));

  if (allFilesHaveQueries && uploaderQueries.every((q) => q.isSuccess)) {
    const unprocessedFilesToProcess = [...unprocessedFiles];
    setUnprocessedFiles([]);

    unprocessedFilesToProcess.forEach((file) => {
      uploadAttachment(file);
    });
  }

  return { handleFilesSelect };
}
